import React, { useState, useMemo } from 'react';
import { Card, Col, Form, Nav, Row, Tab } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import Flex from '../../common/Flex';
import CardDropdown from '../../common/CardDropdown';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const NavItem = ({ item, eventKey }) => {
  return (
    <Nav.Item as="li">
      <Nav.Link
        className="mb-0 py-3 cursor-pointer"
        eventKey={eventKey}
      >
        {item}
      </Nav.Link>
    </Nav.Item>
  );
};

const InvestmentChart = ({ data, type }) => {
  const isBar = type === 'investment' || type === 'performance';
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            // Si el dataset está asignado al eje derecho (y1), formatear como %
            if (context.dataset && context.dataset.yAxisID === 'y1') {
              return `${label}: ${Number(value).toFixed(1)}%`;
            }
            if (type === 'utilization') {
              return `${label}: ${Number(value).toFixed(1)}%`;
            }
            if (type === 'performance') {
              return `${label}: ${Number(value).toFixed(0)}`;
            }
            // Inversión y tendencias por defecto en dinero
            const remaining = context.raw != null && context.dataset && context.dataset.showRemaining
              ? ` | Restante: $${(Number(context.raw.remaining) / 1000).toFixed(1)}K`
              : '';
            return `${label}: $${(Number(value) / 1000).toFixed(1)}K${remaining}`;
          }
        }
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Período'
        },
        stacked: false
      },
      y: {
        display: true,
        title: {
          display: true,
          text: type === 'utilization' ? 'Utilización (%)' : (type === 'performance' ? 'Órdenes' : 'Inversión ($)')
        },
        ticks: {
          callback: function(value) {
            if (type === 'utilization') {
              return `${value}%`;
            }
            if (type === 'performance') {
              return `${value}`;
            }
            return `$${(value / 1000).toFixed(0)}K`;
          }
        },
        beginAtZero: true,
        suggestedMax: type === 'utilization' ? 120 : undefined,
        stacked: false
      },
      y1: {
        display: type === 'investment',
        position: 'right',
        grid: { drawOnChartArea: false },
        min: 0,
        max: 120,
        ticks: {
          callback: function(value) {
            return `${value}%`;
          }
        },
        title: {
          display: true,
          text: 'Utilización (%)'
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
    elements: {
      point: { radius: isBar ? 0 : 2 }
    }
  };

  return (
    <div style={{ height: '300px' }}>
      {isBar ? (
        <Bar data={data} options={chartOptions} />
      ) : (
        <Line data={data} options={chartOptions} />
      )}
    </div>
  );
};

const InvestmentRevenue = ({ orders, assignments }) => {
  const [navItems] = useState([
    { key: 'investment', label: 'Inversión' },
    { key: 'utilization', label: 'Utilización' },
    { key: 'performance', label: 'Performance' },
    { key: 'trends', label: 'Tendencias' }
  ]);

  const [viewType, setViewType] = useState('monthly');
  const [showUtilizationLine, setShowUtilizationLine] = useState(true);

  // Procesar datos para gráficos
  const chartData = useMemo(() => {

    // 1) Construcción mensual base
    const monthlyMap = new Map();

    orders.forEach(order => {
      const startDate = new Date(order.dateFrom);
      const endDate = new Date(order.dateTo);
      const investment = parseFloat(order.netInvestment) || 0;

      let currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      const lastDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

      while (currentDate <= lastDate) {
        const periodKey = currentDate.toISOString().substring(0, 7); // YYYY-MM

        if (!monthlyMap.has(periodKey)) {
          monthlyMap.set(periodKey, {
            totalInvestment: 0,
            totalAssigned: 0,
            ordersCount: 0,
            activeOrders: 0
          });
        }

        const period = monthlyMap.get(periodKey);
        period.totalInvestment += investment;
        period.ordersCount += 1;

        const periodStart = new Date(currentDate);
        const periodEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

        if (startDate <= periodEnd && endDate >= periodStart) {
          period.activeOrders += 1;
        }

        currentDate.setMonth(currentDate.getMonth() + 1);
      }
    });

    // Agregar asignaciones al mapa mensual
    assignments.forEach(assignment => {
      const order = orders.find(o => o.id === assignment.insertionOrderId);
      if (!order) return;

      const assignmentDate = new Date(assignment.createdAt || order.dateFrom);
      const periodKey = assignmentDate.toISOString().substring(0, 7);

      if (monthlyMap.has(periodKey)) {
        let amount = parseFloat(assignment.montoValor) || 0;

        if (assignment.amountType === 'percentage') {
          amount = (amount * parseFloat(order.netInvestment)) / 100;
        }

        monthlyMap.get(periodKey).totalAssigned += amount;
      }
    });

    // 2) Transformar según vista seleccionada
    let processedData;
    if (viewType === 'quarterly') {
      const quarterlyMap = new Map();

      Array.from(monthlyMap.entries()).forEach(([period, data]) => {
        const year = Number(period.substring(0, 4));
        const month = Number(period.substring(5, 7)) - 1;
        const qKey = `${year}-Q${Math.floor(month / 3) + 1}`;

        if (!quarterlyMap.has(qKey)) {
          quarterlyMap.set(qKey, {
            totalInvestment: 0,
            totalAssigned: 0,
            ordersCount: 0,
            activeOrders: 0
          });
        }

        const q = quarterlyMap.get(qKey);
        q.totalInvestment += data.totalInvestment;
        q.totalAssigned += data.totalAssigned;
        q.ordersCount += data.ordersCount;
        q.activeOrders += data.activeOrders;
      });

      processedData = Array.from(quarterlyMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([period, data]) => ({
          period,
          ...data,
          utilization: data.totalInvestment > 0 ? (data.totalAssigned / data.totalInvestment) * 100 : 0
        }));
    } else {
      processedData = Array.from(monthlyMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([period, data]) => ({
          period,
          ...data,
          utilization: data.totalInvestment > 0 ? (data.totalAssigned / data.totalInvestment) * 100 : 0
        }));
    }

    // 3) Etiquetas
    const labels = processedData.map(d => {
      if (viewType === 'quarterly') {
        const [yearStr, q] = d.period.split('-Q');
        const shortYear = yearStr.substring(2);
        return `T${q} ${shortYear}`; // Trimestre en español
      }
      const date = new Date(d.period + '-01');
      return date.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' });
    });

    return {
      investment: {
        labels,
        datasets: [
          {
            label: 'Inversión Planificada',
            data: processedData.map(d => d.totalInvestment),
            borderColor: 'rgb(54, 162, 235)',
            backgroundColor: processedData.map(d => {
              const util = d.totalInvestment > 0 ? (d.totalAssigned / d.totalInvestment) * 100 : 0;
              // Colores por umbral de utilización
              if (util >= 95) return 'rgba(40, 167, 69, 0.8)'; // verde
              if (util >= 80) return 'rgba(54, 162, 235, 0.8)'; // azul
              if (util >= 60) return 'rgba(255, 206, 86, 0.8)'; // amarillo
              return 'rgba(255, 99, 132, 0.8)'; // rojo
            }),
            borderSkipped: false,
            categoryPercentage: 0.6,
            barPercentage: 0.9
          },
          {
            label: 'Inversión Asignada',
            data: processedData.map(d => d.totalAssigned),
            borderColor: 'rgb(255, 99, 132)',
            backgroundColor: 'rgba(255, 99, 132, 0.5)'
          },
          ...(showUtilizationLine ? [{
            // Línea de Utilización sobre eje derecho
            label: 'Utilización (%)',
            data: processedData.map(d => (d.totalInvestment > 0 ? (d.totalAssigned / d.totalInvestment) * 100 : 0)),
            type: 'line',
            yAxisID: 'y1',
            borderColor: 'rgba(255, 159, 64, 1)',
            backgroundColor: 'rgba(255, 159, 64, 0.15)',
            fill: false,
            tension: 0.35,
            pointRadius: 2
          }] : [])
        ]
      },
      utilization: {
        labels,
        datasets: [
          {
            label: 'Utilización de Presupuesto (%)',
            data: processedData.map(d => d.utilization),
            borderColor: 'rgb(75, 192, 192)',
            backgroundColor: 'rgba(75, 192, 192, 0.1)',
            fill: true,
            tension: 0.35,
            pointRadius: 2
          },
          {
            label: 'Meta 85%',
            data: processedData.map(() => 85),
            borderColor: 'rgb(255, 159, 64)',
            backgroundColor: 'rgba(255, 159, 64, 0.0)',
            fill: false,
            borderDash: [6, 6],
            pointRadius: 0
          }
        ]
      },
      performance: {
        labels,
        datasets: [
          {
            label: 'Órdenes Activas',
            data: processedData.map(d => d.activeOrders),
            borderColor: 'rgb(153, 102, 255)',
            backgroundColor: 'rgba(153, 102, 255, 0.5)'
          }
        ]
      },
      trends: {
        labels,
        datasets: [
          {
            label: 'Inversión Total',
            data: processedData.map(d => d.totalInvestment),
            borderColor: 'rgb(54, 162, 235)',
            backgroundColor: 'rgba(54, 162, 235, 0.0)',
            fill: false,
            tension: 0.35,
            borderWidth: 2,
            pointRadius: 0
          },
          {
            label: 'Asignado',
            data: processedData.map(d => d.totalAssigned),
            borderColor: 'rgb(255, 99, 132)',
            backgroundColor: 'rgba(255, 99, 132, 0.0)',
            fill: false,
            tension: 0.35,
            borderWidth: 2,
            pointRadius: 0,
            borderDash: [4, 4]
          }
        ]
      }
    };
  }, [orders, assignments, viewType, showUtilizationLine]);

  // Insights para cliente/asesor basados en Inversión
  const insights = useMemo(() => {
    const inv = chartData.investment;
    if (!inv || !inv.labels || inv.labels.length === 0) return null;

    // Usar datasets de inversión para calcular utilización aun si la línea está oculta
    const planned = inv.datasets.find(d => d.label === 'Inversión Planificada');
    const assigned = inv.datasets.find(d => d.label === 'Inversión Asignada');
    if (!planned || !assigned) return null;

    const utilizations = inv.labels.map((_, i) => {
      const p = Number(planned.data[i]) || 0;
      const a = Number(assigned.data[i]) || 0;
      return p > 0 ? (a / p) * 100 : 0;
    });

    const target = 85;
    const belowTargetCount = utilizations.filter(u => u < target).length;
    let bestIdx = 0; let worstIdx = 0;
    utilizations.forEach((u, i) => {
      if (u > utilizations[bestIdx]) bestIdx = i;
      if (u < utilizations[worstIdx]) worstIdx = i;
    });

    const last3 = utilizations.slice(-3);
    const prev3 = utilizations.slice(-6, -3);
    const avg = arr => arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : 0;
    const trendDelta = avg(last3) - avg(prev3);

    return {
      belowTargetCount,
      best: { label: inv.labels[bestIdx], value: utilizations[bestIdx] },
      worst: { label: inv.labels[worstIdx], value: utilizations[worstIdx] },
      trend: { delta: trendDelta, direction: trendDelta > 1 ? 'up' : (trendDelta < -1 ? 'down' : 'flat') }
    };
  }, [chartData]);

  // Calcular métricas de resumen
  const summaryMetrics = useMemo(() => {
    const totalInvestment = orders.reduce((sum, order) => 
      sum + (parseFloat(order.netInvestment) || 0), 0
    );

    const totalAssigned = assignments.reduce((sum, assignment) => {
      let amount = parseFloat(assignment.montoValor) || 0;
      if (assignment.amountType === 'percentage') {
        const order = orders.find(o => o.id === assignment.insertionOrderId);
        if (order && order.netInvestment) {
          amount = (amount * parseFloat(order.netInvestment)) / 100;
        }
      }
      return sum + amount;
    }, 0);

    return {
      totalInvestment,
      totalAssigned,
      utilization: totalInvestment > 0 ? (totalAssigned / totalInvestment) * 100 : 0,
      remaining: totalInvestment - totalAssigned
    };
  }, [orders, assignments]);

  return (
    <Card className="mt-3">
      <Tab.Container id="investment-tab" defaultActiveKey="investment">
        <Card.Header
          as={Flex}
          justifyContent="between"
          alignItems="center"
          className="ps-0 py-0 border-bottom"
        >
          <Nav
            as="ul"
            className="nav-tabs border-0 flex-nowrap chart-tab tab-active-caret"
          >
            {navItems.map(item => (
              <NavItem item={item.label} eventKey={item.key} key={item.key} />
            ))}
          </Nav>
          <CardDropdown />
        </Card.Header>

        <Card.Body>
          <Row className="g-1">
            <Col xxl={3}>
              <Row className="g-0 my-2">
                <Col md={6} xxl={12}>
                  <div className="border-xxl-bottom border-xxl-200 mb-2">
                    <h2 className="text-primary">${(summaryMetrics.totalInvestment / 1000).toFixed(1)}K</h2>
                    <p className="fs-11 text-500 fw-semibold mb-0">
                      <FontAwesomeIcon
                        icon="circle"
                        className="text-primary me-2"
                      />
                      Inversión Total
                    </p>
                    <p className="fs-11 text-500 fw-semibold">
                      <FontAwesomeIcon
                        icon="circle"
                        className="text-warning me-2"
                      />
                      Meta de Utilización: 85%
                    </p>
                  </div>
                  <Form.Check
                    inline
                    type="radio"
                    id="monthly"
                    label="Mensual"
                    name="viewType"
                    checked={viewType === 'monthly'}
                    onChange={() => setViewType('monthly')}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    id="quarterly"
                    label="Trimestral"
                    name="viewType"
                    checked={viewType === 'quarterly'}
                    onChange={() => setViewType('quarterly')}
                  />
                </Col>
                <Col md={6} xxl={12} className="py-2">
                  <Row className="mx-0">
                    <Col xs={6} className="border-end border-bottom py-3">
                      <h5 className="fw-normal text-600">${(summaryMetrics.totalAssigned / 1000).toFixed(1)}K</h5>
                      <h6 className="text-500 mb-0">Asignado</h6>
                    </Col>
                    <Col xs={6} className="border-bottom py-3">
                      <h5 className="fw-normal text-600">{summaryMetrics.utilization.toFixed(1)}%</h5>
                      <h6 className="text-500 mb-0">Utilización</h6>
                    </Col>
                    <Col xs={6} className="border-end py-3">
                      <h5 className="fw-normal text-600">${(summaryMetrics.remaining / 1000).toFixed(1)}K</h5>
                      <h6 className="text-500 mb-0">Restante</h6>
                    </Col>
                    <Col xs={6} className="py-3">
                      <h5 className="fw-normal text-600">{orders.length}</h5>
                      <h6 className="text-500 mb-0">Órdenes</h6>
                    </Col>
                  </Row>
                  <div className="mt-3">
                    <Form.Check
                      type="switch"
                      id="toggle-utilization-line"
                      label="Mostrar Utilización (%) en Inversión"
                      checked={showUtilizationLine}
                      onChange={(e) => setShowUtilizationLine(e.target.checked)}
                    />
                  </div>
                  {insights && (
                    <div className="mt-3 p-2 border rounded-2">
                      <h6 className="mb-2">Insights</h6>
                      <div className="fs-11 text-600 mb-1">
                        <FontAwesomeIcon icon="exclamation-triangle" className="text-warning me-2" />
                        {insights.belowTargetCount} períodos bajo la meta (85%)
                      </div>
                      <div className="fs-11 text-600 mb-1">
                        <FontAwesomeIcon icon="trophy" className="text-success me-2" />
                        Mejor período: {insights.best.label} — {insights.best.value.toFixed(1)}%
                      </div>
                      <div className="fs-11 text-600 mb-1">
                        <FontAwesomeIcon icon="chart-line" className="text-info me-2" />
                        Tendencia 3 vs 3 previos: {insights.trend.direction === 'up' ? 'al alza' : insights.trend.direction === 'down' ? 'a la baja' : 'estable'} ({insights.trend.delta.toFixed(1)} pp)
                      </div>
                    </div>
                  )}
                </Col>
              </Row>
            </Col>
            <Col xxl={9}>
              <Tab.Content>
                <Tab.Pane unmountOnExit eventKey="investment">
                  <InvestmentChart data={chartData.investment} type="investment" />
                </Tab.Pane>
                <Tab.Pane unmountOnExit eventKey="utilization">
                  <InvestmentChart data={chartData.utilization} type="utilization" />
                </Tab.Pane>
                <Tab.Pane unmountOnExit eventKey="performance">
                  <InvestmentChart data={chartData.performance} type="performance" />
                </Tab.Pane>
                <Tab.Pane unmountOnExit eventKey="trends">
                  <InvestmentChart data={chartData.trends} type="trends" />
                </Tab.Pane>
              </Tab.Content>
            </Col>
          </Row>
        </Card.Body>
      </Tab.Container>
    </Card>
  );
};

export default InvestmentRevenue; 